import cloneDeep from 'lodash/cloneDeep';
import { sha256 } from 'js-sha256';
import leadService from '../services/leadService';
import {
  prepareCreateLead,
  prepareUpdateLead,
  prepareCreateConsumerId,
  prepareCreateFinanceApplication,
} from './factory/leads';

const initialState = {
  consumerId: '',
  consumerJWT: '',
  leadId: '',
  utm: null,
  googleClientId: null,
  customDealerLeadData: null,
  subType: null,
  leadCompleted: null,
  // Tracks the last time we sent a heartbeat
  heartBeatSentTime: null,
  heartBeatSending: null,
  leadExpired: null,
  isCompleting: null,
  coag: {
    ecid: null,
    regionId: null,
  },
  consumerBasicInfo: {
    firstName: '',
    phoneNumber: '',
    productOutcomeStatuses: [],
  },
};

const mutations = {
  SET_CONSUMER_ID(state, data) {
    state.consumerId = data;
  },
  SET_CONSUMER_JWT(state, data) {
    state.consumerJWT = data;
  },
  SET_LEAD_ID(state, data) {
    state.leadId = data;
  },
  SET_UTM_DATA(state, data) {
    // We only set the value when the required values are not null
    if (data.source && data.medium && data.campaign) {
      state.utm = data;
    }
  },
  SET_CUSTOM_DEALER_LEAD_DATA(state, data) {
    state.customDealerLeadData = data;
  },
  SET_GOOGLE_CLIENT_ID(state, data) {
    state.googleClientId = data;
  },
  SET_SUB_TYPE(state, data) {
    state.subType = data;
  },
  SET_LEAD_COMPLETE(state, data) {
    state.leadCompleted = data;
  },
  SET_COMPLETING(state, data) {
    state.isCompleting = data;
  },
  SET_HEART_BEAT_SENT_TIME(state, data) {
    state.heartBeatSentTime = data;
  },
  SET_HEART_BEAT_SENDING(state, data) {
    state.heartBeatSending = data;
  },
  SET_LEAD_EXPIRED(state, data) {
    state.leadExpired = data;
  },
  SET_COAG(state, data) {
    state.coag = {
      ...data,
    };
  },
  SET_CONSUMER_BASIC_INFO(state, data) {
    state.consumerBasicInfo = data;
  },
};

const getters = {
  isLeadCreated: state => state.leadId !== '',
  getLeadId: state => state.leadId,
  isCompleting: state => state.isCompleting,
  leadIsActive: state => state.leadId !== '' && !state.leadExpired && !state.leadCompleted,
  financeApplicationIsComplete: state =>
    state.consumerBasicInfo.productOutcomeStatuses.some(product => product.name === 'financeApplication'),
  financeApplicationIsExpired: state =>
    state.consumerBasicInfo.productOutcomeStatuses.some(product => product.name === 'credit' && product.expired),
};

const actions = {
  async createConsumerId({ commit, rootState }) {
    try {
      const consumerObject = prepareCreateConsumerId(rootState.user, rootState.business);
      const response = await leadService.createConsumerId(consumerObject);
      commit('SET_CONSUMER_ID', response.data.consumerId);
    } catch (e) {
      this._vm.$log('Error creating consumer ID', e);
      throw e;
    }
  },
  async createLead(
    {
      // eslint-disable-next-line no-shadow
      rootState,
      state,
      commit,
      // eslint-disable-next-line no-shadow
      getters,
      dispatch,
    },
    payload
  ) {
    // check if there's a lead created
    let response;
    if (!getters.isLeadCreated) {
      // check if there's a consumer ID created
      if (!rootState.route.query.consumerId) {
        await dispatch('createConsumerId');
      }

      // Set a subtype if provided
      if (payload && payload.subType) {
        commit('SET_SUB_TYPE', payload.subType);
      }

      const leadObject = prepareCreateLead(rootState.business, rootState.route, state, rootState.vehicle);
      response = await leadService.createLead(rootState.lead.consumerId, leadObject);
      const {
        data: { leadId },
      } = response;
      commit('SET_LEAD_ID', leadId);

      // This user data (email and phone number) is sent to the SDK to be sent to the data layer for use in GTM
      // it should not be sent directly to GA events because it is PII
      const { enableUserDataLeadCreatedEvent } = rootState.business.info.integrationSettings;

      const userData = {
        lead_id: leadId,
        ...(enableUserDataLeadCreatedEvent && {
          email_address: rootState.user.emailAddress,
          phone_number: rootState.user.phoneNumber,
          email_address_sha256: sha256(rootState.user.emailAddress),
          phone_number_sha256: sha256(rootState.user.phoneNumber),
        }),
      };

      window.parent.postMessage(
        {
          path: 'lead-created',
          user_data: userData,
        },
        '*'
      );
    }
  },

  async updateLead(
    {
      // eslint-disable-next-line no-shadow
      rootState,
      state,
      // eslint-disable-next-line no-shadow
      getters,
    },
    payload
  ) {
    if (rootState.route.query.returnToFinanceApp === 'true') return;
    const updateLeadObject = prepareUpdateLead(
      rootState.user,
      rootState.vehicle,
      rootState.credit,
      rootState.trade,
      rootState.route,
      state,
      rootState.experiment,
      rootState.paymentCalculator,
      rootState.testDrive,
      rootState.insurance,
      rootState.buildAndPrice,
      rootState.business
    );
    const options = {};
    if (payload && payload.step) {
      options.params = {
        ...payload,
      };
    }
    await leadService.updateLead(updateLeadObject, getters.getLeadId, options);
  },

  // eslint-disable-next-line no-shadow
  async completeLead({ commit, getters, dispatch }) {
    if (!getters.isCompleting && getters.leadIsActive) {
      commit('SET_COMPLETING', true);
      await leadService.completeLead(getters.getLeadId);
      await dispatch('analytics/leadCompleteEvent', null, { root: true });
      commit('SET_LEAD_COMPLETE', true);
      commit('SET_COMPLETING', false);
    }
  },

  // eslint-disable-next-line no-shadow
  async createOffer({ getters }) {
    await leadService.createOffer(getters.getLeadId);
  },

  // eslint-disable-next-line no-shadow
  async sendHeartBeat({ getters }) {
    await leadService.sendHeartBeat(getters.getLeadId);
  },

  // These are custom variables for COAG (CanadaOne Auto Group)
  // that are pulled by the SDK on COAG websites for their internal use of adobe analytics
  // https://mobials.atlassian.net/browse/EC-494
  setECIDAndRegionID({ rootState, commit }) {
    const { ecid, regionId } = rootState.route.query;
    if (ecid || regionId) {
      commit('SET_COAG', { ecid: ecid ?? null, regionId: regionId ?? null });
    }
  },

  async getBasicInfo({ rootState, commit }) {
    const res = await leadService.getConsumerBasicInfo(rootState.lead.consumerId);
    commit('SET_CONSUMER_BASIC_INFO', res.data);
  },

  async sendMobileOtp({ rootState }) {
    await leadService.sendConsumerMobileOtp(rootState.lead.consumerId);
  },

  async verifyMobileOtp({ rootState, commit }, payload) {
    const res = await leadService.verifyConsumerMobileOtp(rootState.lead.consumerId, payload, [
      'forFinanceApplication',
    ]);
    const { annualGrossIncome, homeMonthlyPayment } = res.data.requestedInfo[0].forFinanceApplication;
    commit('user/SET_GROSS_INCOME', annualGrossIncome, { root: true });
    commit('user/SET_MONTHLY_PAYMENT', homeMonthlyPayment, { root: true });
    commit('SET_CONSUMER_JWT', res.data.consumerJWT);
  },

  // eslint-disable-next-line no-shadow
  async createFinanceApplication({ rootState, getters, dispatch }) {
    if (!getters.isLeadCreated) {
      await dispatch('createLead');
    }
    if (getters.leadIsActive) {
      const financeBody = prepareCreateFinanceApplication(rootState.user);
      await leadService.sendFinanceApplication(rootState.lead.consumerId, rootState.lead.leadId, financeBody);
    }
  },

  async updateMetadata({ rootState }) {
    try {
      const payload = { routeList: rootState.routeList };
      await leadService.updateLeadMetadata(rootState.lead.consumerId, rootState.lead.leadId, payload);
    } catch (error) {
      this._vm.$log('Error in updating lead meta data:', error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  actions,
  state: cloneDeep(initialState),
  mutations,
  getters,
};
